import React, { useState, useRef } from "react";
import { Container, Form, Spinner } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import BackButton from "../../../components/UI_Kit/BackButton/BackButton";
import PhotoMetadataModal from '../../../components/Modals/PhotoMetadataModal';

// Import form components
import TypeOneForm from "../Create/Step2/forms/TypeOneForm";
import TypeTwoForm from "../Create/Step2/forms/TypeTwoForm";
import TypeThreeForm from "../Create/Step2/forms/TypeThreeForm";
import TypeFourForm from "../Create/Step2/forms/TypeFourForm";
import CameraConfig from "./EditCameraConfig";

import { getPhotoLocation } from "../../../utils/photoUtils";
import { deleteCameraApi, savePointDataApi } from './editPointApi';
import { useEditPointForm } from '../../../hooks/useEditPointForm';
import { usePointConfigurations } from '../../../hooks/usePointConfigurations'; // Import the new hook

const EditPoint = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { pointToEdit, quotationId } = location.state || {};

  // Use the custom hook for form state management
  const {
    formData,
    setFormData,
    initialCameraIds,
    setInitialCameraIds,
    handleChange
  } = useEditPointForm(pointToEdit, navigate);

  // Use the custom hook for fetching configurations
  const { pointConfigurations, isLoadingConfig, configError } = usePointConfigurations();

  const [saving, setSaving] = useState(false);
  const [showMenu, setShowMenu] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);
  const [editedMetadata, setEditedMetadata] = useState({ name: "", location: "" });
  const fileInputRef = useRef(null);
  const [showCameraConfig, setShowCameraConfig] = useState(false);



  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Process each file to extract metadata
    const processedPhotos = await Promise.all(files.map(async (file) => {
      // Get location data from the photo if available
      const locationData = await getPhotoLocation(file);
      
      // Create location string based on extracted coordinates
      let locationString;
      if (locationData.latitude !== null && locationData.longitude !== null) {
        // Format coordinates to 6 decimal places for consistency
        const lat = parseFloat(locationData.latitude).toFixed(6);
        const lon = parseFloat(locationData.longitude).toFixed(6);
        locationString = `${lat}, ${lon}`;
      } else {
        // Only use default message if no coordinates were found
        locationString = "Aucune donnée de localisation disponible";
      }
      
      return {
        file,
        url: URL.createObjectURL(file),
        name: file.name,
        location: locationString,
        latitude: locationData.latitude,
        longitude: locationData.longitude,
        isNew: true,
        // Store EXIF data for HEIC files if needed
        exifData: file.name.toLowerCase().endsWith('.heic') ? {
          latitude: locationData.latitude,
          longitude: locationData.longitude
        } : null
      };
    }));

    setFormData((prev) => ({
      ...prev,
      photos: [...(prev.photos || []), ...processedPhotos],
    }));
    
    // Show success message with count of photos that have location data
    const photosWithLocation = processedPhotos.filter(p => p.latitude !== null && p.longitude !== null).length;
    if (photosWithLocation > 0) {
      toast.success(`Ajout de ${processedPhotos.length} photo(s). Données de localisation extraites de ${photosWithLocation} photo(s).`);
    } else {
      toast.info(`${processedPhotos.length} photo(s) ajoutée(s). Aucune donnée de localisation n'a pu être extraite.`);
    }
  };

  const handleDeletePhoto = (index) => {
    setFormData((prev) => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index),
    }));
  };

  const handleOpenModal = (index) => {
    setSelectedPhotoIndex(index);
    if (formData && formData.photos && formData.photos[index]) {
      setEditedMetadata({
        name: formData.photos[index].name || "",
        location: formData.photos[index].location || "",
      });
    } else {
      setEditedMetadata({ name: "", location: "" });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPhotoIndex(null);
  };

  const handleMetadataChange = (e) => {
    const { name, value } = e.target;
    setEditedMetadata((prev) => ({ ...prev, [name]: value }));
  };

  const handleReplacePhoto = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;
    const locationData = await getPhotoLocation(file);
    let locationString = formData.photos[index].location;
    if (locationData.latitude !== null && locationData.longitude !== null) {
      locationString = `${locationData.latitude}, ${locationData.longitude}`;
    }

    // Create a new photo object with the file, URL and location data
    const updatedPhoto = {
      ...formData.photos[index],
      file,
      url: URL.createObjectURL(file),
      location: locationString,
      latitude: locationData.latitude,
      longitude: locationData.longitude,
      isNew: !formData.photos[index].id, // Correctly set isNew based on existing photo ID
      // Store EXIF data for HEIC files if needed
      exifData: file.name.toLowerCase().endsWith('.heic') ? {
        latitude: locationData.latitude,
        longitude: locationData.longitude
      } : null
    };

    // Update the photos array with the new photo
    setFormData((prev) => ({
      ...prev,
      photos: prev.photos.map((photo, i) => (i === index ? updatedPhoto : photo)),
    }));
    
    // Update the edited metadata to show the new location in the modal
    if (selectedPhotoIndex === index) {
      setEditedMetadata(prev => ({
        ...prev,
        location: locationString // Use the extracted/updated locationString
      }));
    }
  };

  const handleSaveMetadata = () => {
    if (selectedPhotoIndex === null) return;
    setFormData((prev) => ({
      ...prev,
      photos: prev.photos.map((photo, i) => {
        if (i === selectedPhotoIndex) {
          return { ...photo, name: editedMetadata.name, location: editedMetadata.location };
        }
        return photo;
      }),
    }));
    handleCloseModal();
    toast.success("Les métadonnées de la photo ont été mises à jour avec succès");
  };

  const getCurrentPhotos = () => {
    return formData?.photos || [];
  };

  const handleSavePoint = async () => {
    if (showCameraConfig) return;
    if (formData.hasCameras === "yes") { // Uses formData from the hook
      setShowCameraConfig(true);
      return;
    }
    setSaving(true);
    // Pass formData and initialCameraIds from the hook
    const success = await savePointDataApi(formData, quotationId, initialCameraIds, navigate);
    if (success) {
      console.log("Point data saved successfully");
    }
    setSaving(false);
  };

  const deleteCamera = async (cameraId) => {
    const success = await deleteCameraApi(quotationId, pointToEdit.id, cameraId);
    if (success) {
      // Update local state using setters from the hook
      setFormData(prev => ({
        ...prev,
        cameras: prev.cameras.filter(c => c.id !== cameraId)
      }));
      setInitialCameraIds(prev => prev.filter(id => id !== cameraId));
    }
  };

  const handleCameraSave = async (updatedCameras) => {
    // Use formData and setFormData from the hook
    const updatedFormData = { ...formData, cameras: updatedCameras };
    setFormData(updatedFormData);
    setShowCameraConfig(false);
    setSaving(true);
    // Pass updatedFormData and initialCameraIds from the hook
    const success = await savePointDataApi(updatedFormData, quotationId, initialCameraIds, navigate);
    if (success) {
      console.log("Point data saved successfully");
    }
    setSaving(false);
  };

  // Render the correct form based on point type, passing the config
  const renderFormBasedOnType = () => {
    if (isLoadingConfig) { // Use isLoadingConfig from hook
      return (
        <>
          <Skeleton height={50} count={1} style={{ marginBottom: "1rem" }} />
          <Skeleton height={100} count={1} style={{ marginBottom: "1rem" }} />
          <Skeleton height={100} count={1} style={{ marginBottom: "1rem" }} />
          <Skeleton height={50} count={5} style={{ marginBottom: '1rem' }} />
        </>
      );
    }
    if (configError) { // Use configError from hook
      return (
        <p className="text-danger">
          Error loading form configuration: {configError}
        </p>
      );
    }
    if (!formData || !formData.pointType) {
      return <p>Loading form data...</p>;
    }

    const config = pointConfigurations[formData.pointType]; // Use pointConfigurations from hook

    if (!config) {
      console.warn(
        `No configuration found for point type: ${formData.pointType}`
      );
      // Optionally render a message or default form
    }

    switch (formData.pointType) {
      case "vision_point":
        return (
          <TypeOneForm
            config={config} // Pass config from hook
            type1Data={formData}
            handleChange={handleChange}
            fileInputRef={fileInputRef}
            handlePhotoUpload={handlePhotoUpload}
            showMenu={showMenu}
            setShowMenu={setShowMenu}
            handleOpenModal={handleOpenModal}
            handleDeletePhoto={handleDeletePhoto}
          />
        );
      case "relay_point":
        return (
          <TypeTwoForm
            config={config} // Pass config from hook
            type2Data={formData}
            handleChange={handleChange}
            fileInputRef={fileInputRef}
            handlePhotoUpload={handlePhotoUpload}
            showMenu={showMenu}
            setShowMenu={setShowMenu}
            handleOpenModal={handleOpenModal}
            handleDeletePhoto={handleDeletePhoto}
          />
        );
      case "high_point":
        return (
          <TypeThreeForm
            config={config} // Pass config from hook
            type3Data={formData}
            handleChange={handleChange}
            fileInputRef={fileInputRef}
            handlePhotoUpload={handlePhotoUpload}
            showMenu={showMenu}
            setShowMenu={setShowMenu}
            handleOpenModal={handleOpenModal}
            handleDeletePhoto={handleDeletePhoto}
          />
        );
      case "surveillance_center":
        return (
          <TypeFourForm
            config={config} // Pass config from hook
            type4Data={formData}
            handleChange={handleChange}
            fileInputRef={fileInputRef}
            handlePhotoUpload={handlePhotoUpload}
            showMenu={showMenu}
            setShowMenu={setShowMenu}
            handleOpenModal={handleOpenModal}
            handleDeletePhoto={handleDeletePhoto}
          />
        );
      default:
        return (
          <p>Unknown point type ({formData.pointType}) or form not loaded.</p>
        );
    }
  };

  if (!formData) {
    // Replace the main Spinner with a Skeleton loader
    return (
      <Container className="pt-4 d-flex justify-content-center">
        <>
          {" "}
          {/* Wrap Skeleton for better layout control */}
          <Skeleton height={40} style={{ marginBottom: "1rem" }} />
          <Skeleton height={200} count={2} style={{ marginBottom: "1rem" }} />
          <Skeleton height={50} />
        </>
      </Container>
    );
  }

  return (
    <HelmetProvider>
      <ToastContainer />
      <Helmet>
        <title>Edit Point - ProtectLease</title>
        <meta name="description" content="Edit point details" />
      </Helmet>

      <Container className="pt-4 d-flex justify-content-center">
        <div className="container-wrap w-100">
          <div className="header mb-4 d-flex align-items-center">
            <BackButton className="back-button" />
            <span className="step-title">Modifier le point</span>
          </div>

          <Form onSubmit={(e) => e.preventDefault()} style={{marginBottom:"100px"}}>
            {showCameraConfig ? (
              <CameraConfig
                initialCameras={formData.cameras || []}
                onSave={handleCameraSave} // Pass the new handler
                deleteCamera={deleteCamera} // Pass the deleteCamera function
              />
            ) : (
              <>
                {renderFormBasedOnType()} {/* Render the dynamic form */}
                <div className="footer shadow-lg d-flex justify-content-center">
                  <button
                    className="w-100 primary-button primary-button-block"
                    onClick={handleSavePoint} // This button now triggers camera config if needed, or saves directly
                    disabled={saving || isLoadingConfig} // Disable while saving or loading config
                  >
                    {saving ? (
                      <>
                        <Spinner // Keep the Spinner for the button saving state
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                        /> {" "}
                        Sauvegarde...
                      </>
                    ) : (
                      "Sauvegarder les modifications"
                    )}
                  </button>
                </div>
              </>
            )}
          </Form>
        </div>
      </Container>

      {/* Use the new PhotoMetadataModal component */}
      <PhotoMetadataModal
        show={showModal}
        handleClose={handleCloseModal}
        handleConfirm={handleSaveMetadata}
        selectedPhotoIndex={selectedPhotoIndex}
        getCurrentPhotos={getCurrentPhotos}
        handleReplacePhoto={handleReplacePhoto}
        editedMetadata={editedMetadata}
        handleMetadataChange={handleMetadataChange}
      />

    </HelmetProvider>
  );
};

export default EditPoint;